import React from 'react'
import { Filter, Search } from 'lucide-react'
import { WORK_TYPE_OPTIONS } from '../../lib/workTypes'
import type { Issue, Label, ProjectMember, WorkType } from '../../types'
import type { IssueFilters } from '../../api/issues'

type Filters = IssueFilters

interface IssueFiltersBarProps {
  filters: Filters
  onChange: (filters: Filters) => void
  members: ProjectMember[]
  labels: Label[]
  currentUserId?: string
  resultCount: number
}

const PRIORITY_OPTIONS: { value: Issue['priority']; label: string }[] = [
  { value: 'URGENT', label: 'Urgent' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
  { value: 'NO_PRIORITY', label: 'No priority' },
]

const setDateRange = (current: Filters, prefix: 'created' | 'due', from: string, to: string): Filters => {
  const fromKey = `${prefix}From` as 'createdFrom' | 'dueFrom'
  const toKey = `${prefix}To` as 'createdTo' | 'dueTo'
  return { ...current, [fromKey]: from || undefined, [toKey]: to || undefined }
}

export default function IssueFiltersBar({ filters, onChange, members, labels, currentUserId, resultCount }: IssueFiltersBarProps) {
  const update = (patch: Filters) => onChange({ ...filters, ...patch })
  const clear = () => onChange({})
  const hasFilters = Object.values(filters).some(Boolean)
  const assignedToMe = Boolean(currentUserId && filters.assignee === currentUserId)

  return (
    <div style={wrapStyle}>
      <div style={topRowStyle}>
        <div style={searchWrapStyle}>
          <Search size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
          <input
            value={filters.search || ''}
            onChange={(event) => update({ search: event.target.value || undefined })}
            placeholder="Search tasks..."
            style={searchInputStyle}
          />
        </div>

        <button
          type="button"
          onClick={() => update({ assignee: assignedToMe ? undefined : currentUserId })}
          disabled={!currentUserId}
          style={{
            ...quickButtonStyle,
            ...(assignedToMe ? quickButtonActiveStyle : {}),
          }}
        >
          Assigned to me
        </button>

        <div style={resultStyle}>
          <Filter size={14} />
          {resultCount} task{resultCount === 1 ? '' : 's'}
        </div>

        {hasFilters && (
          <button type="button" onClick={clear} style={clearButtonStyle}>
            Clear
          </button>
        )}
      </div>

      <div style={filterGridStyle}>
        <label style={fieldStyle}>
          <span style={labelStyle}>Assignee</span>
          <select value={filters.assignee || ''} onChange={(event) => update({ assignee: event.target.value || undefined })} style={inputStyle}>
            <option value="">All assignees</option>
            {members.map((member) => (
              <option key={member.userId} value={member.userId}>{member.user.fullName}</option>
            ))}
          </select>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Due date</span>
          <div style={datePairStyle}>
            <input
              type="date"
              value={filters.dueFrom || ''}
              onChange={(event) => onChange(setDateRange(filters, 'due', event.target.value, filters.dueTo || ''))}
              style={inputStyle}
            />
            <input
              type="date"
              value={filters.dueTo || ''}
              onChange={(event) => onChange(setDateRange(filters, 'due', filters.dueFrom || '', event.target.value))}
              style={inputStyle}
            />
          </div>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Created date</span>
          <div style={datePairStyle}>
            <input
              type="date"
              value={filters.createdFrom || ''}
              onChange={(event) => onChange(setDateRange(filters, 'created', event.target.value, filters.createdTo || ''))}
              style={inputStyle}
            />
            <input
              type="date"
              value={filters.createdTo || ''}
              onChange={(event) => onChange(setDateRange(filters, 'created', filters.createdFrom || '', event.target.value))}
              style={inputStyle}
            />
          </div>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Task type</span>
          <select value={filters.workType || ''} onChange={(event) => update({ workType: (event.target.value || undefined) as WorkType | undefined })} style={inputStyle}>
            <option value="">All types</option>
            {WORK_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Label</span>
          <select value={filters.label || ''} onChange={(event) => update({ label: event.target.value || undefined })} style={inputStyle}>
            <option value="">All labels</option>
            {labels.map((label) => (
              <option key={label.id} value={label.id}>{label.name}</option>
            ))}
          </select>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Priority</span>
          <select value={filters.priority || ''} onChange={(event) => update({ priority: event.target.value || undefined })} style={inputStyle}>
            <option value="">All priorities</option>
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  )
}

const wrapStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  background: '#ffffff',
  padding: 12,
  marginBottom: 16,
  flexShrink: 0,
}

const topRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 10,
}

const searchWrapStyle: React.CSSProperties = {
  flex: '1 1 280px',
  minWidth: 220,
  height: 38,
  border: '1px solid #d7dce3',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 10px',
  background: '#ffffff',
}

const searchInputStyle: React.CSSProperties = {
  border: 'none',
  outline: 'none',
  fontSize: 13,
  color: '#111827',
  flex: 1,
  minWidth: 0,
  fontFamily: 'inherit',
}

const quickButtonStyle: React.CSSProperties = {
  height: 38,
  border: '1px solid #d7dce3',
  borderRadius: 8,
  background: '#f8fafc',
  color: '#475569',
  padding: '0 12px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

const quickButtonActiveStyle: React.CSSProperties = {
  background: '#6366f1',
  borderColor: '#6366f1',
  color: '#ffffff',
}

const resultStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: '#64748b',
  fontSize: 12.5,
  whiteSpace: 'nowrap',
}

const clearButtonStyle: React.CSSProperties = {
  height: 32,
  border: 'none',
  borderRadius: 7,
  background: '#f1f5f9',
  color: '#475569',
  padding: '0 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
}

const filterGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(140px, 1fr) minmax(190px, 1.25fr) minmax(190px, 1.25fr) minmax(135px, 0.9fr) minmax(135px, 0.9fr) minmax(135px, 0.9fr)',
  gap: 10,
  alignItems: 'end',
}

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  minWidth: 0,
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#64748b',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 36,
  border: '1px solid #d7dce3',
  borderRadius: 8,
  padding: '0 9px',
  fontSize: 12.5,
  color: '#111827',
  background: '#ffffff',
  outline: 'none',
  boxSizing: 'border-box',
}

const datePairStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 6,
}
